
import { apiService } from "@services/api.service";
import { computed, defineComponent, getCurrentInstance, onMounted, Ref, ref } from "vue";
import { CreateToast } from "@utils/toasts";
import { NeverFallback } from "vue/types/vue";
import { RegistrationConfirmation } from "@pictograms";
import { settingsService } from "@services/settings.service";
import { useI18n } from "@composables/i18n";
import { useLoader } from "@composables/loader";
import { Vue } from "vue-property-decorator";
import InlineSvg from "vue-inline-svg";
import { ROUTENAMES } from "@models/constants/routes";
import { useRouter } from "vue-router/composables";

export default defineComponent({
  name: "CiConfirmEmail",
  components: {
    "inline-svg": InlineSvg,
  },
  props: {
    inModalContext: { type: Boolean, default: false },
    returnButtonText: { type: String, default: "" },
  },
  setup(props) {
    const loader = useLoader();
    const router = useRouter();
    const { t } = useI18n(getCurrentInstance());
    const root: Ref<NeverFallback<never, Vue> | undefined> = ref();
    const confirmationReminder: ImportedSVG = RegistrationConfirmation;
    const imageSize = computed(() => (props.inModalContext ? "150px" : "200px"));

    const articleRegistration = computed(
      () => router.resolve(localRedirectPath.value).route.name == ROUTENAMES.CONTENTS_SHOW
    );
    const backLinkText = computed(() =>
      props.returnButtonText === ""
        ? t("ui.components.users.onboarding.register.back_to_default")
        : props.returnButtonText
    );
    const redirectUrl = computed(
      () => settingsService.options.registration.redirect_url || settingsService.options.registration.root_path
    );
    const localRedirectPath = computed(() => {
      if (redirectUrl.value.startsWith("/") && !redirectUrl.value.startsWith("http")) return redirectUrl.value;

      const url = new URL(redirectUrl.value);
      if (url.hostname === settingsService.options.region.host) return url.pathname;

      return settingsService.options.registration.root_path;
    });

    const closeModal = () => {
      apiService.refreshSettings();
      if (props.inModalContext) {
        root.value?.$bvModal.hide("modal-registration");
        root.value?.$bvModal.hide("modal-registration-soft-gating");
      } else {
        // location.href bc we want to reload to get the full navigation
        const whereTo =
          settingsService.options.registration.redirect_url || settingsService.options.registration.root_path;
        window.location.href = whereTo;
      }
    };

    const resendConfirmationMail = async () => {
      try {
        loader.start();
        await apiService.axios.get(settingsService.options.registration.resend_email_path);
        CreateToast.success(t("ui.components.users.onboarding.register.resent_email") as string);
      } catch (err) {
        CreateToast.error(t("error_occurred") as string);
      } finally {
        loader.stop();
      }
    };

    onMounted(() => (root.value = getCurrentInstance()?.proxy.$root));

    return {
      articleRegistration,
      backLinkText,
      closeModal,
      confirmationReminder,
      imageSize,
      resendConfirmationMail,
    };
  },
});
